import { Euler, Mesh, Object3D, SkinnedMesh, Vector3 } from "three";
import { GameResourcesPackage } from "../../../resources/GameResourcesPackage";
import { TweenUtils } from "../../../../../tween/TweenUtils";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { Ticker } from "pixi.js";
import { GameViewModel } from "../../../models/GameViewModel";
import { IProjectileContainer } from "../gamefield/IProjectileContainer";
import { TurretProjectile } from "../projectiles/TurretProjectile";
import { Event } from "event-system";
import { BaseMole } from "../gamefield/mole/BaseMole";
import { AudioController, Sfx } from "../../AudioController";
import { AttackState } from "../gamefield/mole/states/AttackState";
import { IdleTimeoutState } from "../gamefield/mole/states/IdleTimeoutState";
import { IdleToRepeatState } from "../gamefield/mole/states/IdleToRepeatState";
import { EatingState } from "../gamefield/mole/states/EatingState";
import { FreezeState } from "../gamefield/mole/states/FreezeState";
import { TurretBoosterData } from "../../../../../common/models/data/TurretBoosterData";
import { TurretAnimationController } from "./TurretAnimationController";
import { GloryHole } from "../gamefield/GloryHole";

export class Turret extends Object3D {
    public readonly timeOut: Event;
    private readonly targetMolesStates = [AttackState, IdleTimeoutState, IdleToRepeatState, EatingState, FreezeState] as const;
    private readonly turretObject: Object3D;
    public set turretObjectVisible(value: boolean) {
        this.turretObject.visible = value;
    }
    private readonly headMesh: SkinnedMesh;
    private readonly bodyMesh: SkinnedMesh;
    private readonly projectilesSpawnPoint: Object3D;
    private readonly turretRotationBone: Object3D;
    private readonly animationController: TurretAnimationController;
    private initialTurretRotation: Euler;
    private hole: GloryHole;
    private targetLocked: boolean;
    private currentAttackInterval: number;
    private lifetime: number;
    private target: BaseMole;

    public constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly audioController: AudioController,
        private readonly gameViewModel: GameViewModel,
        private readonly turretConfig: TurretBoosterData,
        private readonly projectileContainer: IProjectileContainer
    ) {
        super();
        this.hole = new GloryHole(clone(gameResources.assets_game_Hole_glb.scene.children[0]) as Mesh);
        this.turretObject = clone(gameResources.assets_game_Turret_glb.scene.getObjectByName("Armature"));
        this.turretObject.visible = false;
        this.bodyMesh = this.turretObject.getObjectByName("BasisTurret") as SkinnedMesh;
        this.bodyMesh.castShadow = true;
        this.bodyMesh.receiveShadow = true;
        this.headMesh = this.turretObject.getObjectByName("HeadTurret") as SkinnedMesh;
        this.headMesh.castShadow = true;
        this.headMesh.receiveShadow = true;
        this.projectilesSpawnPoint = this.turretObject.getObjectByName("ShootPosition1");
        this.turretRotationBone = this.turretObject.getObjectByName("HeadTurret_Rotation");
        this.animationController = new TurretAnimationController(this, this.headMesh, this.gameResources.assets_game_Turret_glb.animations);

        this.currentAttackInterval = turretConfig.attackInterval;
        this.lifetime = turretConfig.lifeTime;
        this.targetLocked = false;
        this.target = null;
        this.timeOut = new Event();

        this.add(this.hole, this.turretObject);
        this.addEventListener("added", this.onAdded);
    }

    private async onAdded(): Promise<void> {
        await this.hole.open();
        await this.animationController.playSpawn();
        this.initialTurretRotation = this.rotation.clone();
        Ticker.shared.add(this.onTick, this);
    }

    private onTick(_dt: number): void {
        this.lifetime = Math.max(this.lifetime - Ticker.shared.deltaMS, 0);
        if (this.lifetime === 0) {
            this.timeOut.emit();
            return;
        }
        if (this.targetLocked) {
            this.currentAttackInterval = Math.max(this.currentAttackInterval - Ticker.shared.deltaMS, 0);
            if (this.currentAttackInterval == 0) {
                this.currentAttackInterval = this.turretConfig.attackInterval;
                if (this?.target?.alive) {
                    this.fire();
                } else {
                    this.target = null;
                    this.acquireNextTarget();
                }
            }
        } else {
            if (this.target == null) {
                this.acquireNextTarget();
            }
        }
    }

    private async acquireNextTarget(): Promise<void> {
        this.targetLocked = false;
        const possibleTargets = this.gameViewModel.aliveSpawnedMoles.filter(mole => {
            return this.targetMolesStates.some(cls => mole.state instanceof cls);
        });
        this.target = possibleTargets[0];
        if (this.target == null) {
            return;
        }
        const rotationAngle = this.getRotationAngle(this.turretRotationBone, this.target.getWorldPosition(new Vector3()));
        await this.rotateTo(rotationAngle);
        this.targetLocked = true;
    }

    private getRotationAngle(turretHead: Object3D, target: Vector3): number {
        const headPos = turretHead.getWorldPosition(new Vector3());

        const toTarget = target.clone().sub(headPos);
        toTarget.y = 0;

        if (toTarget.lengthSq() === 0) return turretHead.rotation.y;

        toTarget.normalize();

        const angle = Math.atan2(toTarget.x, toTarget.z);

        return Math.max(-Math.PI / 2, Math.min(Math.PI / 2, angle));
    }

    private async rotateTo(angle: number): Promise<void> {
        const current = this.turretRotationBone.rotation.y;
        const delta = angle - current;

        if (Math.abs(delta) < 1e-3) {
            return;
        }

        const rotationSpeed = 0.8;
        const duration = (Math.abs(delta) / rotationSpeed) * 1000;

        await TweenUtils.tweenTo(this.turretRotationBone.rotation, { y: angle }, duration);
    }

    private fire(): void {
        this.animationController.playShoot();
        this.audioController.playSfx(Sfx.TurretShoot);
        const projectile = new TurretProjectile(this.projectileContainer, clone(this.gameResources.assets_game_Weapons_glb.scene.children[3]) as Mesh, 0.3, this.turretConfig.damage);
        this.projectilesSpawnPoint.add(projectile);
        const direction = this.target.head.getWorldPosition(new Vector3()).sub(projectile.getWorldPosition(new Vector3())).normalize();
        projectile.throwAtDirection(direction);
    }

    public cancel(): void {
        Ticker.shared.remove(this.onTick, this);
    }

    public async destroy(): Promise<void> {
        this.removeEventListener("added", this.onAdded);
        Ticker.shared.remove(this.onTick, this);
        await this.rotateTo(this.initialTurretRotation.y);
        await this.animationController.playHide();
        this.animationController.destroy();
        await this.hole.close();
        this.removeFromParent();
    }
}
